import { useMemo, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { adminApi, type AdminDevice } from '../../api/admin';

const POLL_MS = 30_000;

export function DevicesTab() {
  const [hostFilter, setHostFilter] = useState('');
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);

  const key = 'admin/devices';
  const { data: devices = [], isLoading } = useSWR<AdminDevice[]>(
    key,
    () => adminApi.listDevices(),
    { refreshInterval: POLL_MS },
  );

  const filtered = useMemo(() => {
    if (!hostFilter) return devices;
    const q = hostFilter.toLowerCase();
    return devices.filter(
      (d) =>
        d.hostname.toLowerCase().includes(q) ||
        d.username.toLowerCase().includes(q) ||
        (d.primary_mac ?? '').toLowerCase().includes(q),
    );
  }, [devices, hostFilter]);

  const totals = useMemo(() => {
    let active = 0;
    let stale = 0;
    let disabled = 0;
    const dayAgo = Date.now() - 24 * 3600 * 1000;
    for (const d of devices) {
      if (!d.enabled) disabled++;
      else if (
        d.last_seen_at &&
        new Date(d.last_seen_at.replace(' ', 'T') + 'Z').getTime() > dayAgo
      )
        active++;
      else stale++;
    }
    return { active, stale, disabled };
  }, [devices]);

  async function toggleEnabled(d: AdminDevice) {
    setActionErr(null);
    setActionMsg(null);
    try {
      await adminApi.setDeviceEnabled(d.id, !d.enabled);
      setActionMsg(`${d.hostname}: ${!d.enabled ? 'enabled' : 'disabled'}`);
      void mutate(key);
    } catch (err) {
      setActionErr(err instanceof Error ? err.message : String(err));
    }
  }

  async function remove(d: AdminDevice) {
    if (!window.confirm(`Remove ${d.hostname} (${d.username})?\nIts token stops working immediately.`)) return;
    setActionErr(null);
    setActionMsg(null);
    try {
      await adminApi.deleteDevice(d.id);
      setActionMsg(`Removed ${d.hostname}.`);
      void mutate(key);
    } catch (err) {
      setActionErr(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-cockpit-dim">
            Devices ({devices.length})
          </h3>
          <p className="text-xs text-cockpit-dim">
            Office workstations reporting hostname, user, MAC. Active (24h) {totals.active} ·
            stale {totals.stale} · disabled {totals.disabled}.
          </p>
        </div>
        <label className="ml-auto flex flex-col text-xs">
          <span className="mb-1 text-cockpit-dim">Filter (host / user / MAC)</span>
          <input
            type="text"
            value={hostFilter}
            onChange={(e) => setHostFilter(e.target.value)}
            placeholder="filter…"
            className="rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 font-mono text-cockpit-text"
          />
        </label>
      </div>

      {actionMsg && (
        <div className="rounded border border-cockpit-ok/40 bg-cockpit-ok/10 px-3 py-2 text-xs text-cockpit-ok">
          {actionMsg}
        </div>
      )}
      {actionErr && (
        <div className="rounded border border-cockpit-crit/40 bg-cockpit-crit/10 px-3 py-2 text-xs text-cockpit-crit">
          {actionErr}
        </div>
      )}

      <div className="overflow-x-auto rounded border border-cockpit-border">
        <table className="w-full min-w-[1200px] text-sm">
          <thead className="bg-cockpit-panel text-xs uppercase tracking-wider text-cockpit-dim">
            <tr>
              <th className="px-3 py-2 text-left">Hostname</th>
              <th className="px-3 py-2 text-left">User</th>
              <th className="px-3 py-2 text-left">Primary MAC</th>
              <th className="px-3 py-2 text-left">Last IP</th>
              <th className="px-3 py-2 text-left">Geo</th>
              <th className="px-3 py-2 text-left">First seen</th>
              <th className="px-3 py-2 text-left">Last seen</th>
              <th className="px-3 py-2 text-right">Reports</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => (
              <DeviceRow key={d.id} d={d} onToggle={() => void toggleEnabled(d)} onRemove={() => void remove(d)} />
            ))}
            {filtered.length === 0 && !isLoading && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-sm text-cockpit-dim">
                  no devices match the filter
                  {devices.length === 0 ? (
                    <>
                      {' '}
                      — deploy the registration script via Group Policy to start seeing entries.
                    </>
                  ) : null}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-cockpit-dim">
        Workstations enrol via <code className="font-mono">POST /api/devices/enrol</code> (one-time, returns a token) then report every hour via <code className="font-mono">POST /api/devices/report</code> with the token in <code className="font-mono">X-Device-Token</code>. See the deployment script in the repo.
      </p>
    </div>
  );
}

function DeviceRow({
  d,
  onToggle,
  onRemove,
}: {
  d: AdminDevice;
  onToggle: () => void;
  onRemove: () => void;
}) {
  const geo = [d.last_geo_city, d.last_geo_country].filter(Boolean).join(', ') || '—';
  const dayAgo = Date.now() - 24 * 3600 * 1000;
  const seenRecently =
    d.last_seen_at && new Date(d.last_seen_at.replace(' ', 'T') + 'Z').getTime() > dayAgo;
  const statusLabel = !d.enabled ? 'disabled' : seenRecently ? 'active' : 'stale';
  const statusTone = !d.enabled
    ? 'text-cockpit-dim'
    : seenRecently
      ? 'text-cockpit-ok'
      : 'text-cockpit-warn';

  return (
    <tr className="border-t border-cockpit-border align-top">
      <td className="px-3 py-2 font-mono text-xs">{d.hostname}</td>
      <td className="px-3 py-2 font-mono text-xs">{d.username}</td>
      <td className="px-3 py-2 font-mono text-xs">{d.primary_mac ?? '—'}</td>
      <td className="px-3 py-2 font-mono text-xs">{d.last_ip ?? '—'}</td>
      <td className="px-3 py-2 text-xs">{geo}</td>
      <td className="px-3 py-2 text-xs">{fmtTime(d.first_seen_at)}</td>
      <td className="px-3 py-2 text-xs">{fmtTime(d.last_seen_at)}</td>
      <td className="px-3 py-2 text-right font-mono text-xs">{d.report_count}</td>
      <td className={`px-3 py-2 text-xs uppercase tracking-wider ${statusTone}`}>{statusLabel}</td>
      <td className="px-3 py-2 text-right">
        <div className="flex justify-end gap-1">
          <button
            type="button"
            onClick={onToggle}
            className="rounded border border-cockpit-border px-2 py-0.5 text-[11px] uppercase tracking-wider text-cockpit-dim hover:bg-cockpit-border/30"
            title={d.enabled ? 'Stop accepting reports from this device' : 'Re-enable this device'}
          >
            {d.enabled ? 'Disable' : 'Enable'}
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="rounded border border-cockpit-crit/40 px-2 py-0.5 text-[11px] uppercase tracking-wider text-cockpit-crit hover:bg-cockpit-crit/10"
            title="Forget this device — its token stops working"
          >
            Remove
          </button>
        </div>
      </td>
    </tr>
  );
}

function fmtTime(ts: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}
